/**
 * holy-nav-localize.js — local/global nav switching
 *
 * Visitors in the Salt Lake City DMA get a "Visit The Ritual Bar" link
 * appended to the primary nav (both desktop teaser + mobile drawer).
 * Non-local visitors see the default Primary Global nav unchanged.
 *
 * The two Shopify menus (main-menu = "Primary Global", primary-local =
 * "Primary Local") differ ONLY by that one link. So instead of swapping
 * the entire rendered menu (which would require forking the Wonder theme
 * section), we render the default Global menu server-side and inject the
 * one extra link client-side when geo says local.
 *
 * Geo path (V1):
 *   1. Check localStorage for cached DMA decision (7-day TTL)
 *   2. If no cache, fetch from ipapi.co (free, no key, 1k req/day)
 *   3. Decide: state code === 'UT' → 'slc' DMA. Else 'other'.
 *   4. Cache result.
 *   5. If 'slc', inject the Visit-The-Ritual-Bar link into both navs.
 *
 * Future iteration:
 *   - Replace ipapi.co with an AXIS-engine endpoint that does MaxMind
 *     GeoLite2 + ZIP-to-DMA mapping. Removes the third-party call and
 *     gives true Nielsen-DMA-level precision (county boundaries).
 *   - Refine SLC DMA = UT + parts of ID/NV/WY by ZIP prefix.
 */

(() => {
  'use strict';

  const STORAGE_KEY     = 'holy_dma';
  const STORAGE_TS_KEY  = 'holy_dma_at';
  const CACHE_TTL_MS    = 7 * 24 * 60 * 60 * 1000;  // 7 days
  const LOCAL_BAR_URL   = '/pages/bar-daybreak';
  const LOCAL_BAR_LABEL = 'Visit The Ritual Bar';
  const MARKER_CLASS    = 'holy-local-bar-link';

  // V1: any UT visitor counts as local. Future: tighten to SLC DMA counties.
  function isSlcDma(geo) {
    return geo && geo.region_code === 'UT';
  }

  function readCache() {
    try {
      const v = localStorage.getItem(STORAGE_KEY);
      const t = parseInt(localStorage.getItem(STORAGE_TS_KEY) || '0', 10);
      if (v && (Date.now() - t) < CACHE_TTL_MS) return v;
    } catch (e) {}
    return null;
  }

  function writeCache(dma) {
    try {
      localStorage.setItem(STORAGE_KEY, dma);
      localStorage.setItem(STORAGE_TS_KEY, String(Date.now()));
    } catch (e) {}
  }

  async function resolveDma() {
    const cached = readCache();
    if (cached) return cached;

    try {
      const resp = await fetch('https://ipapi.co/json/', { headers: { Accept: 'application/json' } });
      if (!resp.ok) return 'other';
      const geo = await resp.json();
      const dma = isSlcDma(geo) ? 'slc' : 'other';
      writeCache(dma);
      return dma;
    } catch (e) {
      return 'other';
    }
  }

  // ─── Desktop nav — sits to the LEFT of the localization triggers container, as a
  //     real anchor (NOT a wt-localization-trigger — that class binds the country-drawer
  //     opener + uses a larger font size). Color + underline match USD via custom.css
  //     using `color: currentColor` + a clean text-decoration on hover. Size stays
  //     small + inline-friendly via the inline style block below.
  function injectDesktop() {
    if (document.querySelector('.' + MARKER_CLASS)) return;

    const localizationTriggers = document.querySelector(
      '.wt-header__localization__triggers, .wt-header__localization__triggers--menu-drawer'
    );

    if (localizationTriggers && localizationTriggers.parentNode) {
      const a = document.createElement('a');
      a.href = LOCAL_BAR_URL;
      a.className = `wt-header__action-link ${MARKER_CLASS}`;
      a.setAttribute('data-holy-local-bar', '');
      a.textContent = LOCAL_BAR_LABEL;
      a.style.cssText = [
        'display: inline-flex',
        'align-items: center',
        'padding: 0 16px',
        'font-size: 13px',
        'letter-spacing: 0.04em',
        'white-space: nowrap',
        'height: 100%'
      ].join('; ');
      localizationTriggers.parentNode.insertBefore(a, localizationTriggers);
      return;
    }

    // Fallback: legacy teaser-list append if right-side anchor not found.
    const list = document.querySelector('.wt-header__nav-teaser__list');
    if (!list || list.querySelector('.' + MARKER_CLASS)) return;
    const li = document.createElement('li');
    li.className = `wt-header__nav-teaser__item ${MARKER_CLASS}`;
    const a = document.createElement('a');
    a.href = LOCAL_BAR_URL;
    a.className = 'wt-header__nav-teaser__link';
    const span = document.createElement('span');
    span.className = 'wt-header__nav-teaser__link-text wt-page-nav-mega__decorated';
    span.textContent = LOCAL_BAR_LABEL;
    a.appendChild(span);
    li.appendChild(a);
    list.appendChild(li);
  }

  // ─── Mobile drawer mega-menu ────────────────────────────────────────
  // Wonder structure: <ul class="wt-page-nav-mega__list"><li class="wt-page-nav-mega__item">…</li>
  // The drawer renders its own mega-menu inside `.wt-drawer--nav`.
  function injectMobile() {
    const drawerLists = document.querySelectorAll(
      '.wt-drawer--nav .wt-page-nav-mega__list, .wt-drawer--mobile-nav .wt-page-nav-mega__list'
    );
    drawerLists.forEach((list) => {
      if (list.querySelector('.' + MARKER_CLASS)) return;
      const li = document.createElement('li');
      li.className = `wt-page-nav-mega__item ${MARKER_CLASS}`;
      const a = document.createElement('a');
      a.href = LOCAL_BAR_URL;
      a.className = 'wt-page-nav-mega__link wt-page-nav-mega__link--parent';
      const span = document.createElement('span');
      span.className = 'wt-page-nav-mega__link__text wt-page-nav-mega__link__text--underline';
      span.textContent = LOCAL_BAR_LABEL;
      a.appendChild(span);
      li.appendChild(a);
      list.appendChild(li);
    });
  }

  function inject() {
    injectDesktop();
    injectMobile();
  }

  async function main() {
    const dma = await resolveDma();
    if (dma !== 'slc') return;

    inject();

    // Drawer + sticky-header mega-menus may render late. Watch for them.
    const obs = new MutationObserver(() => inject());
    obs.observe(document.body, { childList: true, subtree: true });

    // Belt-and-suspenders: try again a few times for theme JS that may
    // rebuild the nav on resize / sticky-attach.
    setTimeout(inject, 500);
    setTimeout(inject, 1500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', main);
  } else {
    main();
  }
})();
